import axios, { type AxiosRequestConfig, type AxiosResponse } from "axios";
import { getAdminEnvironmentConfig } from "../services/adminEnvironment";
import { getAdminAccessToken, installAdminAuthInterceptor } from "../services/adminAuthSession";

/**
 * Common client for the Spring Boot admin API.
 *
 * Keep the token/header and API base URL consistent for all admin calls.
 */
export const springAdminApi = axios.create({
  baseURL: getAdminEnvironmentConfig().apiBaseUrl,
});

springAdminApi.interceptors.request.use((config) => {
  const token = getAdminAccessToken();

  if (token) {
    config.headers.set("access-token", token);
  }

  return config;
});

installAdminAuthInterceptor(springAdminApi);

export type SpringListResponse<T> = {
  data: T[];
  total?: number;
  message?: string;
  pagination?: unknown;
};

export type SpringItemResponse<T> = {
  data: T;
  message?: string;
};

export type SpringErrorResponse = {
  message?: string;
  detail?: string;
  statusCode?: number;
  errors?: unknown;
};

const adminPath = (path: string) =>
  `/admin/${path.replace(/^\/+/, "")}`;

export const springAdminRequest = {
  get: <T>(path: string, config?: AxiosRequestConfig) =>
    springAdminApi.get<T>(adminPath(path), config),

  post: <T>(path: string, body?: unknown, config?: AxiosRequestConfig) =>
    springAdminApi.post<T>(adminPath(path), body, config),

  put: <T>(path: string, body?: unknown, config?: AxiosRequestConfig) =>
    springAdminApi.put<T>(adminPath(path), body, config),

  patch: <T>(path: string, body?: unknown, config?: AxiosRequestConfig) =>
    springAdminApi.patch<T>(adminPath(path), body, config),

  delete: <T>(path: string, config?: AxiosRequestConfig) =>
    springAdminApi.delete<T>(adminPath(path), config),

  upload: <T>(path: string, formData: FormData, config?: AxiosRequestConfig) =>
    springAdminApi.post<T>(adminPath(path), formData, config),
};

export type SpringAdminResponse<T> = AxiosResponse<T>;
